<template>
  <div class="container-fluid admin-page">
    <div class="title-area">
      <h2>事業所管理</h2>
      <div class="create-btn" v-if="canCreateGroup2">
        <button class="btn btn-info" @click="create">
          <i class="fas fa-plus"></i><span class="ml-1">新規作成</span>
        </button>
      </div>
    </div>
    <AdminListTable
      :display-data="filteredGroups2"
      :fields="fields"
      :can-edit="canEditGroup2"
      :can-delete="canDeleteGroup2"
      @start-edit="edit"
      @start-destroy="tryDestroy"
    />
    <Modal
      v-if="showEditModal"
      :title="editModalTitle"
      bottom-close-button-label="保存"
      :isCloseButtonDisabled="validateEditting.length > 0"
      @close="save"
      @dismiss="showEditModal = false"
    >
      <template v-slot:body>
        <div>
          <div class="form-group">
            <label class="form-control-label">
              事業所名
            </label>
            <input type="text" class="form-control" v-model="editting.name" />
          </div>
          <div class="form-group">
            <label class="form-control-label">
              表示順
            </label>
            <input type="text" class="form-control" v-model="editting.dispOrder" />
          </div>
          <div class="form-group errors">
            <div class="error" v-for="(e, i) in httpErrorMsgs" :key="i">
              {{e}}
            </div>
          </div>
        </div>
      </template>
    </Modal>
    <Modal
      v-if="showDestroyModal"
      :title="destroyModalTitle"
      :show-bottom-dismiss-button="true"
      @close="doDestroy"
      @dismiss="showDestroyModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p1">事業所名<span class="colon"></span>{{editting.name}}</p>
          <p class="p2 font-mtx-desc" v-html="destroyModalMsg"></p>
        </div>
      </template>
    </Modal>
    <Modal
      v-if="showErrorModal"
      :title="errorModalTitle"
      :show-bottom-dismiss-button="false"
      @close="showErrorModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p2 font-mtx-desc" v-html="errorModalMsg"></p>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import group2Api from 'src/apis/group2';
import type { AdminListTableField } from 'src/models/adminListTable';
import type { AdminGroup2 } from 'src/models/group2';
import {
  computed,
  onMounted,
  ref,
} from 'vue';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import type { AxiosError } from 'axios';
import { useAuth } from 'src/composables/useAuth';

interface AdminGroup2Edit {
  name: AdminGroup2['name'];
  dispOrder: AdminGroup2['dispOrder'];
}

interface AdminGroup2Update extends AdminGroup2Edit {
  id: AdminGroup2['id'];
}

const initEditState = (): AdminGroup2Edit => {
  return {
    name: '',
    dispOrder: 0,
  };
};

const showEditModal = ref(false);
const editMode = ref<'create' | 'edit'>('edit');
const showDestroyModal = ref(false);
const showErrorModal = ref(false);
const errorModalMsg = ref('不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。');
const groups2Orig = ref<AdminGroup2[]>([]);
const filteredGroups2 = ref<AdminGroup2[]>([]);
const editting = ref<AdminGroup2Edit | AdminGroup2Update>(initEditState());
const httpErrorMsgs = ref<string[]>([]);

const validateEditting = computed<Array<string>>(() => {
  let errorMsgs = [];
  if (editting.value.name.length <= 0) {
    errorMsgs.push('事業所名は必須です');
  }
  if (!/^\d+$/.test(editting.value.dispOrder.toString())) {
    errorMsgs.push('表示順は数値で入力して下さい');
  }
  return errorMsgs;
});
const editModalTitle = computed<string>(() => editMode.value === 'create' ? '新規作成' : '編集');

const { user } = useAuth();

const canCreateGroup2 = computed(() => {
  return user?.hasRoleOperationsAdmin ?? false;
});
const canEditGroup2 = () => {
  return user?.hasRoleBranchAdmin ?? false;
};
const canDeleteGroup2 = () => {
  return user?.hasRoleBranchAdmin ?? false;
};

const doSearch = () => {
  resetSearchResults();
  group2Api.adminIndex()
    .then((groups2) => {
      if (!groups2 || groups2.length === 0) return;

      groups2Orig.value = groups2;
      filterSearch();
    });
};
const filterSearch = () => {
  filteredGroups2.value = groups2Orig.value;
};
const resetSearchResults = () => {
  filteredGroups2.value = [];
};
onMounted(doSearch);

const edit = (obj: AdminGroup2Update) => {
  editting.value = {
    id: obj.id,
    name: obj.name,
    dispOrder: obj.dispOrder,
  };
  httpErrorMsgs.value = [];
  showEditModal.value = true;
  editMode.value = 'edit';
};

const create = () => {
  let maxDispOrder = 0;
  groups2Orig.value.forEach(e => {
    if (!isNaN(e.dispOrder) && maxDispOrder < e.dispOrder) {
      maxDispOrder = e.dispOrder;
    }
  });
  editting.value = {
    name: '',
    dispOrder: maxDispOrder + 1,
  };
  httpErrorMsgs.value = [];
  showEditModal.value = true;
  editMode.value = 'create';
};

const save = async() => {
  try {
    if (editMode.value === 'edit') {
      await group2Api.adminUpdate((editting.value as AdminGroup2Update).id, {
        name: editting.value.name,
        disp_order: Number(editting.value.dispOrder),
      });
    } else {
      await group2Api.adminCreate({
        name: editting.value.name,
        disp_order: Number(editting.value.dispOrder),
      });
    }
    httpErrorMsgs.value = [];
    showEditModal.value = false;
    doSearch();
  } catch (err) {
    httpErrorMsgs.value = getErrorMessages(err as AxiosError);
  }
};

const tryDestroy = (obj: AdminGroup2Edit) => {
  editting.value = obj;
  showDestroyModal.value = true;
};
const doDestroy = () => {
  group2Api.adminDestroy(
    (editting.value as AdminGroup2Update).id,
  ).then(() => {
    showDestroyModal.value = false;
    doSearch();
  }).catch((err) => {
    const errMsgs = getErrorMessages(err as AxiosError);
    if (errMsgs.includes('has group3s')) {
      errorModalMsg.value = '1件以上の部署が所属しているので削除できません。';
    } else {
      errorModalMsg.value = '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。';
    }
    showDestroyModal.value = false;
    showErrorModal.value = true;
  });
};

const destroyModalTitle = '削除';
const destroyModalMsg = 'を削除してもよろしいですか？';
const errorModalTitle = 'エラー';
const fields: AdminListTableField<AdminGroup2>[] = [
  { name: 'name', label: '事業所名' },
  { name: 'dispOrder', label: '表示順' },
];
</script>

<style lang="scss"  scoped>

</style>
